// Calculated fields as columns.
//
// A report's calculated fields (Calculated Fields tab) are added to their data model's column
// lists as virtual columns, so everything that picks a column - a metric's breakdown and extra
// breakdowns, its "include / exclude these values" lists and value groups, its filters
// ("only include / leave out rows where") and Categorization rule conditions - can pick a
// calculated field exactly as it picks a catalog column, and every SQL builder that reads
// `column.sql` writes the field's expression in its place.
//
//   key          "calc:<field id>"  (calcColumnKey; matched by this key only)
//   sql          "(<the field's expression>)"
//   dataType     decimal / nvarchar / int / date, from what the field evaluates to, so the
//                operators offered (numbers: >, between ...; dates: before, after ...) fit it
//   valueOptions fixed values for date parts (Monday ... Sunday, Q1 ... Q4, ...)
//
// Only COMPLETE fields become columns: an unfinished field has no SQL to write. A saved
// reference to a field that is unfinished, deleted or on another model therefore resolves to
// no column, and validation says why (calculatedColumnReferenceIssue).
//
// Kept out of: the Full Row Detail column grid (that metric has its own Calculated Columns
// panel), the distinct-count column, rule "Set" targets and "Set value from a column" (see
// catalogResolution.ts), the operands of other calculated fields, and the timestamp lists
// (reporting period basis, Period Date, minutes between).
//
// The server builds the same columns from the same field definitions
// (ReportSqlBuilder.CalculatedColumns.cs) - keep the two in step.
import type { CalculatedField, DataColumn, DataModel, MetricSection, ModelKey, ReportConfig, RuleNode, RuleSet } from "../types";
import type { ReportLogicContext } from "./context";
import { calcColumnKey, calculatedFieldIdFromKey, isCalculatedColumn, isCalculatedColumnKey, withoutCalculatedColumns } from "./catalogResolution";
import { calculatedFieldSql, calculatedFieldValueType, calculatedFields, findCalculatedField, isDatePartCalculatedField } from "./calculatedFields";
import { datePartValueOptions } from "./dateParts";

export const CALCULATED_COLUMN_ROLE = "calculated";
const SORT_ORDER_BASE = 1_000_000;

const VALUE_TYPE_SQL: Record<string, { dataType: string; dataTypeCategory: string }> = {
    number: { dataType: "decimal", dataTypeCategory: "numeric" },
    text: { dataType: "nvarchar", dataTypeCategory: "text" },
    date: { dataType: "date", dataTypeCategory: "date" },
};

/** The virtual column of one field, or null when the field has no SQL yet. */
export function calculatedColumnForField(context: ReportLogicContext, report: ReportConfig, field: CalculatedField, index = 0): DataColumn | null {
    const sql = calculatedFieldSql(context, report, field);
    if (!sql) return null;
    const valueType = calculatedFieldValueType(field);
    const types = VALUE_TYPE_SQL[valueType];
    // Whole-number date parts (Year, Week Of Month ...) are "int": equal / in-list values are
    // then compared as numbers on the server.
    const dataType = isDatePartCalculatedField(field) && valueType === "number" ? "int" : types.dataType;
    return {
        key: calcColumnKey(field.id),
        label: (field.name || "").trim() || "Calculated Field",
        sql: `(${sql})`,
        role: CALCULATED_COLUMN_ROLE,
        dataType,
        dataTypeCategory: types.dataTypeCategory,
        isBreakdownable: true,
        isDetailSelectable: false,
        isPeriodAnchor: false,
        isOtpComparable: false,
        isSensitive: false,
        sortOrder: SORT_ORDER_BASE + index,
        calculatedFieldId: field.id,
        valueOptions: isDatePartCalculatedField(field) ? datePartValueOptions(field) : [],
    };
}

/** The report's complete calculated fields on `modelKey`, as columns. */
export function calculatedColumnsForModel(context: ReportLogicContext, report: ReportConfig | null | undefined, modelKey: ModelKey | ""): DataColumn[] {
    if (!report || !modelKey || !report.models.includes(modelKey)) return [];
    return calculatedFields(report)
        .map((field, index) => field.model === modelKey ? calculatedColumnForField(context, report, field, index) : null)
        .filter((column): column is DataColumn => !!column);
}

// --- The data models with calculated columns ------------------------------------------

function stripModel(model: DataModel): DataModel {
    const hasCalc = model.categoryColumns.some(isCalculatedColumn) || !!model.visibleColumns?.some(isCalculatedColumn);
    if (!hasCalc) return model;
    return {
        ...model,
        categoryColumns: withoutCalculatedColumns(model.categoryColumns),
        visibleColumns: model.visibleColumns ? withoutCalculatedColumns(model.visibleColumns) : undefined,
    };
}

const strippedCache = new WeakMap<Record<ModelKey, DataModel>, Record<ModelKey, DataModel>>();

/** The catalog without any calculated columns (idempotence: a context is never extended twice). */
export function catalogDataModels(dataModels: Record<ModelKey, DataModel>) {
    const cached = strippedCache.get(dataModels);
    if (cached) return cached;
    const keys = Object.keys(dataModels);
    const stripped = keys.some((key) => dataModels[key] !== stripModel(dataModels[key]))
        ? keys.reduce<Record<ModelKey, DataModel>>((acc, key) => {
            acc[key] = stripModel(dataModels[key]);
            return acc;
        }, {})
        : dataModels;
    strippedCache.set(dataModels, stripped);
    // An extended map strips back to its catalog in one step next time.
    if (stripped !== dataModels) strippedCache.set(stripped, stripped);
    return stripped;
}

function baseContext(context: ReportLogicContext, dataModels: Record<ModelKey, DataModel>): ReportLogicContext {
    return {
        dataModels,
        lookupGroups: context.lookupGroups,
        lookupOptionsByKey: context.lookupOptionsByKey,
        ruleOperators: context.ruleOperators,
        today: context.today,
        nextId: context.nextId,
    };
}

// One entry per catalog object: rebuilt only when the catalog, the report's fields or its
// models change (reports are replaced, never mutated, on every edit).
const cache = new WeakMap<Record<ModelKey, DataModel>, { fields: CalculatedField[] | undefined; models: string; result: Record<ModelKey, DataModel> }>();

/**
 * The data models with `report`'s complete calculated fields added: to categoryColumns (the
 * pool the primary breakdown picker draws from) and, when the catalog sent one, to
 * visibleColumns (breakdown, filter and condition pools). Columns of other reports are removed.
 */
export function dataModelsWithCalculatedColumns(context: ReportLogicContext, report: ReportConfig | null | undefined): Record<ModelKey, DataModel> {
    const catalog = catalogDataModels(context.dataModels);
    const fields = report?.calculatedFields;
    if (!report || !fields?.length) return catalog;
    const models = report.models.join("|");
    const cached = cache.get(catalog);
    if (cached && cached.fields === fields && cached.models === models) return cached.result;

    const base = baseContext(context, catalog);
    const result = { ...catalog };
    report.models.forEach((modelKey) => {
        const model = catalog[modelKey];
        const columns = model ? calculatedColumnsForModel(base, report, modelKey) : [];
        if (!model || !columns.length) return;
        result[modelKey] = {
            ...model,
            categoryColumns: [...model.categoryColumns, ...columns],
            visibleColumns: model.visibleColumns ? [...model.visibleColumns, ...columns] : undefined,
        };
    });
    cache.set(catalog, { fields, models, result });
    strippedCache.set(result, catalog);
    return result;
}

/** `context` with exactly `report`'s calculated columns - what every SQL builder for that report reads. */
export function contextWithCalculatedColumns(context: ReportLogicContext, report: ReportConfig | null | undefined): ReportLogicContext {
    const dataModels = dataModelsWithCalculatedColumns(context, report);
    return dataModels === context.dataModels ? context : baseContext(context, dataModels);
}

// --- References: where a report uses a field as a column ------------------------------

export interface CalculatedColumnUse {
    /** Metric id, or rule set id for a categorization rule. */
    ownerId: string;
    ownerName: string;
    kind: "metric" | "categorization";
    /** e.g. "breakdown", "extra breakdown", "filter", "rule condition". */
    where: string;
}

function nodesUse(nodes: RuleNode[] | undefined, key: string): boolean {
    return (nodes || []).some((node) => (node as any).type === "group"
        ? nodesUse((node as any).conditions, key)
        : (node as any).column === key || (node as any).compareColumn === key);
}

function periodDateUses(section: MetricSection, key: string) {
    return (section.periodDate?.cases || []).some((item) => nodesUse(item.conditions, key))
        || nodesUse(section.periodFilter?.conditions, key);
}

/** Every place `report` uses calculated field `fieldId` as a column (not as a detail column or measure). */
export function calculatedColumnUses(report: ReportConfig, fieldId: string): CalculatedColumnUse[] {
    const key = calcColumnKey(fieldId);
    const uses: CalculatedColumnUse[] = [];
    report.sections.forEach((section) => {
        const add = (where: string) => uses.push({ ownerId: section.id, ownerName: section.name || "Untitled Metric", kind: "metric", where });
        if (section.breakdownColumn === key) add("breakdown");
        if (section.extraBreakdowns.some((extra) => extra.column === key && extra.source !== "periodDate")) add("extra breakdown");
        if (section.filterColumn === key || section.excludeFilterColumn === key
            || nodesUse(section.filterConditions, key) || nodesUse(section.excludeFilterConditions, key)) add("filter");
        if (periodDateUses(section, key)) add("Period Date / period condition");
    });
    report.bucketRuleSets.forEach((ruleSet: RuleSet) => {
        if (ruleSet.rules.some((rule) => nodesUse(rule.conditions, key))) {
            uses.push({ ownerId: ruleSet.id, ownerName: ruleSet.name || "Untitled Categorization", kind: "categorization", where: "rule condition" });
        }
    });
    return uses;
}

function withoutNodes(nodes: RuleNode[] | undefined, key: string): RuleNode[] | undefined {
    if (!nodes) return nodes;
    return nodes
        .filter((node) => (node as any).type === "group" || ((node as any).column !== key && (node as any).compareColumn !== key))
        .map((node) => (node as any).type === "group" ? { ...(node as any), conditions: withoutNodes((node as any).conditions, key) } : node);
}

/**
 * Removes every column-style use of `fieldId` from the report: a breakdown on it is cleared
 * (the metric then asks for a column), extra breakdowns on it are dropped and conditions
 * testing it are removed. Detail columns and measures are handled by withoutCalculatedField.
 */
export function withoutCalculatedColumnUses(report: ReportConfig, fieldId: string): ReportConfig {
    const key = calcColumnKey(fieldId);
    return {
        ...report,
        sections: report.sections.map((section) => {
            const breakdownCleared = section.breakdownColumn === key;
            return {
                ...section,
                breakdownColumn: breakdownCleared ? "" : section.breakdownColumn,
                include: breakdownCleared ? [] : section.include,
                exclude: breakdownCleared ? [] : section.exclude,
                groups: breakdownCleared ? [] : section.groups,
                extraBreakdowns: section.extraBreakdowns.filter((extra) => extra.source === "periodDate" || extra.column !== key),
                filterColumn: section.filterColumn === key ? "" : section.filterColumn,
                filterValues: section.filterColumn === key ? [] : section.filterValues,
                excludeFilterColumn: section.excludeFilterColumn === key ? "" : section.excludeFilterColumn,
                excludeFilterValues: section.excludeFilterColumn === key ? [] : section.excludeFilterValues,
                filterConditions: withoutNodes(section.filterConditions, key),
                excludeFilterConditions: withoutNodes(section.excludeFilterConditions, key),
                periodDate: section.periodDate
                    ? { ...section.periodDate, cases: section.periodDate.cases.map((item) => ({ ...item, conditions: withoutNodes(item.conditions, key) || [] })) }
                    : section.periodDate,
                periodFilter: section.periodFilter
                    ? { ...section.periodFilter, conditions: withoutNodes(section.periodFilter.conditions, key) || [] }
                    : section.periodFilter,
            };
        }),
        bucketRuleSets: report.bucketRuleSets.map((ruleSet) => ({
            ...ruleSet,
            rules: ruleSet.rules.map((rule) => ({ ...rule, conditions: withoutNodes(rule.conditions, key) || [] })),
        })),
    };
}

/**
 * Why a saved column key that names a calculated field does not resolve on `modelKey`, or
 * null when it is not a calculated-field key (or it resolves).
 */
export function calculatedColumnReferenceIssue(context: ReportLogicContext, report: ReportConfig, modelKey: ModelKey | "", columnKey: string): string | null {
    if (!isCalculatedColumnKey(columnKey)) return null;
    const field = findCalculatedField(report, calculatedFieldIdFromKey(columnKey));
    if (!field) return "It uses a calculated field that no longer exists. Choose another column.";
    const name = (field.name || "").trim() || "The calculated field";
    if (modelKey && field.model !== modelKey) return `"${name}" is calculated on a different data model. Choose another column.`;
    const base = baseContext(context, catalogDataModels(context.dataModels));
    if (!calculatedFieldSql(base, report, field)) return `"${name}" is not complete yet. Finish it on the Calculated Fields tab.`;
    return null;
}
